// Which keys sit on which bar, and in what order, for every toolbar in both
// apps. Each app keeps one layout holding all of its bars, so moving a key
// from one bar to another is a single change that syncs as one.
//
// A key may sit on several bars, but only once on each; a key on no bar is
// switched off. Dividers ('sep:n') and push-apart gaps ('gap:n') are pieces
// the writer places, each with its own number.

import { TOOL_IDS, type ToolId } from './toolSettings'

export type BarDevice = 'desktop' | 'phone'

/** Bars that run across the window take every key; side bars only take keys that fit. */
type BarShape = 'across' | 'side'

export interface BarDefinition {
  id: string
  label: string
  shape: BarShape
}

export const BARS: Record<BarDevice, readonly BarDefinition[]> = {
  desktop: [
    { id: 'top', label: 'Top bar', shape: 'across' },
    { id: 'bottom', label: 'Bottom bar', shape: 'across' },
    { id: 'right', label: 'Right bar', shape: 'side' },
    // Drawn only with tabs set to In the toolbar: the row takes the top bar's place.
    { id: 'tabRow', label: 'Tab row', shape: 'across' },
    { id: 'tabDrawer', label: 'More tools drawer', shape: 'across' },
  ],
  phone: [
    { id: 'keyboard', label: 'Keyboard bar', shape: 'across' },
    { id: 'top', label: 'Top bar', shape: 'across' },
    { id: 'side', label: 'Side bar', shape: 'side' },
    { id: 'selection', label: 'Selected-text bar', shape: 'across' },
    { id: 'tap', label: 'Tap menu', shape: 'across' },
  ],
}

export type BarKeyGroup = 'corpus' | 'type' | 'marks' | 'paragraph' | 'insert' | 'edit' | 'view' | 'status' | 'tools' | 'window'

export interface BarKey {
  id: string
  label: string
  group: BarKeyGroup
  /** Too wide for a side bar. */
  acrossOnly?: boolean
  /** Offered in one app only. */
  device?: BarDevice
  /** Kept at the end of these bars, and allowed on no others. */
  pinnedTo?: readonly string[]
  /** A shorter name, for bars that show words instead of icons. */
  short?: string
}

const TOOL_LABELS: Record<ToolId, string> = {
  calendar: 'Calendar',
  goals: 'Goals',
  'loose-ends': 'Loose Ends',
}

/** The key that opens a tool. */
const toolKey = (tool: ToolId) => `tool:${tool}`

/** The phone key that opens the tool used last (or the first, as Settings > Tools says). */
export const TOOLS_KEY = 'tools'

const BAR_KEYS: readonly BarKey[] = [
  { id: 'panelToggle', label: 'Corpus panel', group: 'corpus', short: 'Panel' },
  { id: 'undo', label: 'Undo', group: 'corpus' },
  { id: 'redo', label: 'Redo', group: 'corpus' },
  { id: 'history', label: 'Version history', group: 'corpus', short: 'History' },
  { id: 'fileMenu', label: 'File menu', group: 'corpus' },

  { id: 'blockType', label: 'Block format', group: 'type', short: 'Block' },
  { id: 'fontFamily', label: 'Font family', group: 'type' },
  { id: 'fontSize', label: 'Font size', group: 'type' },
  { id: 'textColor', label: 'Text color', group: 'type' },
  { id: 'highlightColor', label: 'Highlight color', group: 'type' },
  { id: 'fontWeights', label: 'Font weights', group: 'type' },

  { id: 'bold', label: 'Bold', group: 'marks' },
  { id: 'italic', label: 'Italic', group: 'marks' },
  { id: 'underline', label: 'Underline', group: 'marks' },
  { id: 'strike', label: 'Strikethrough', group: 'marks', short: 'Strike' },
  { id: 'subscript', label: 'Subscript', group: 'marks' },
  { id: 'superscript', label: 'Superscript', group: 'marks' },
  { id: 'code', label: 'Inline code', group: 'marks' },
  { id: 'clearFormatting', label: 'Clear formatting', group: 'marks', short: 'Clear' },

  { id: 'alignLeft', label: 'Align left', group: 'paragraph', short: 'Left' },
  { id: 'alignCenter', label: 'Align center', group: 'paragraph', short: 'Center' },
  { id: 'alignRight', label: 'Align right', group: 'paragraph', short: 'Right' },
  { id: 'alignJustify', label: 'Justify', group: 'paragraph' },
  { id: 'bulletList', label: 'Bullet list', group: 'paragraph', short: 'Bullets' },
  { id: 'orderedList', label: 'Numbered list', group: 'paragraph', short: 'Numbers' },
  { id: 'taskList', label: 'Task list', group: 'paragraph', short: 'Tasks' },

  { id: 'horizontalRule', label: 'Divider line', group: 'insert', short: 'Line' },
  { id: 'link', label: 'Link', group: 'insert' },
  { id: 'image', label: 'Image', group: 'insert' },
  { id: 'table', label: 'Table tools', group: 'insert' },
  { id: 'details', label: 'Collapsible section', group: 'insert', short: 'Section' },
  { id: 'looseEnd', label: 'Loose end', group: 'insert' },
  { id: 'parkIdea', label: 'Park an idea', group: 'insert', short: 'Idea' },
  { id: 'dragHandle', label: 'Block drag mode', group: 'insert', short: 'Drag' },

  { id: 'cut', label: 'Cut', group: 'edit' },
  { id: 'copy', label: 'Copy', group: 'edit' },
  { id: 'paste', label: 'Paste', group: 'edit' },
  { id: 'pastePlain', label: 'Paste as plain text', group: 'edit', short: 'Paste Plain' },
  { id: 'selectAll', label: 'Select all', group: 'edit' },
  { id: 'lookupReference', label: 'Look up', group: 'edit' },
  { id: 'findInPage', label: 'Find in page', group: 'edit' },
  { id: 'referenceManager', label: 'References & aliases', group: 'edit', short: 'References' },
  { id: 'jumpTop', label: 'Go to top', group: 'edit' },
  { id: 'jumpBottom', label: 'Go to bottom', group: 'edit' },

  { id: 'docName', label: 'Document title', group: 'view', acrossOnly: true, device: 'desktop' },
  { id: 'fontPicker', label: 'Page font', group: 'view', acrossOnly: true },
  { id: 'colorPickers', label: 'Page and ink colors', group: 'view', acrossOnly: true },
  { id: 'widthSlider', label: 'Page width slider', group: 'view', acrossOnly: true, device: 'desktop' },
  { id: 'notesView', label: 'Notes switch', group: 'view', acrossOnly: true, device: 'desktop' },
  { id: 'pageView', label: 'Page View switch', group: 'view', acrossOnly: true, device: 'desktop' },
  { id: 'editorMode', label: 'Editor Mode switch', group: 'view', acrossOnly: true, device: 'desktop' },

  { id: 'wordCount', label: 'Word count', group: 'status' },
  { id: 'goal', label: 'Goal readout', group: 'status', acrossOnly: true, device: 'desktop' },
  { id: 'sprint', label: 'Sprint timer', group: 'status', acrossOnly: true, device: 'desktop' },
  { id: 'looseEndCount', label: 'Loose-end count', group: 'status', device: 'desktop' },
  { id: 'saveStatus', label: 'Save status', group: 'status', device: 'desktop' },
  { id: 'syncStatus', label: 'Sync status', group: 'status', device: 'desktop' },
  { id: 'account', label: 'Account', group: 'status', device: 'desktop' },

  // The phone opens a tool over the page; its Tools key opens the one used last.
  { id: TOOLS_KEY, label: 'Tools', group: 'tools', device: 'phone' },
  ...TOOL_IDS.map((tool): BarKey => ({ id: toolKey(tool), label: TOOL_LABELS[tool], group: 'tools' })),

  // The tab row stands in for the top bar, so the window controls may sit on it too.
  { id: 'windowControls', label: 'Window controls', group: 'window', acrossOnly: true, device: 'desktop', pinnedTo: ['top', 'tabRow'] },
  { id: 'moreTools', label: 'More tools', group: 'window', acrossOnly: true, device: 'desktop', pinnedTo: ['tabRow'] },
]

const KEYS_BY_ID = new Map(BAR_KEYS.map((key) => [key.id, key]))

export function barKey(id: string): BarKey | undefined {
  return KEYS_BY_ID.get(id)
}

export function barKeysFor(device: BarDevice): BarKey[] {
  return BAR_KEYS.filter((key) => !key.device || key.device === device)
}

export function isBarPiece(id: string): boolean {
  return /^(sep|gap):\d+$/.test(id)
}

/** Whether a key may go on a bar at all, leaving aside copies already there. */
export function canPlaceKey(device: BarDevice, keyId: string, barId: string): boolean {
  const key = KEYS_BY_ID.get(keyId)
  const bar = BARS[device].find((b) => b.id === barId)
  if (!key || !bar || (key.device && key.device !== device)) return false
  if (key.pinnedTo && !key.pinnedTo.includes(barId)) return false
  return !(key.acrossOnly && bar.shape === 'side')
}

/** Where a switched-off key goes back to: its bar, after the item it last followed. */
interface BarHome {
  bar: string
  after: string | null
}

export interface BarLayout {
  /** What each bar shows, in order. */
  bars: Record<string, string[]>
  /** Switched-off keys and where they last sat. */
  home: Record<string, BarHome>
  /** Every key this layout has accounted for. A key missing here arrived in an update. */
  seen: string[]
}

// '|' is a divider and '~' a push-apart gap; each becomes a numbered piece.
type BarSpec = Record<string, readonly string[]>

const TOOL_KEYS = TOOL_IDS.map(toolKey)

// The desktop top and bottom bars as they were before keys could move.
const DESKTOP_TOP = [
  'panelToggle', '|', 'undo', 'redo', '|',
  'blockType', 'fontFamily', 'fontSize', '|', 'textColor', 'highlightColor', '|',
  'bold', 'italic', 'underline', 'strike', 'subscript', 'superscript', 'code', 'clearFormatting', '|',
  'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', '|',
  'bulletList', 'orderedList', 'taskList', '|',
  'horizontalRule', 'link', 'image', 'details', 'looseEnd', 'dragHandle', 'table', 'history', '|',
  'wordCount', '|', 'fileMenu', '|', 'windowControls',
]
const DESKTOP_BOTTOM = [
  'docName', '|', 'fontPicker', '|', 'colorPickers', '|', 'widthSlider', '|',
  'notesView', '|', 'pageView', '|', 'editorMode', '|',
  'wordCount', '|', 'goal', '|', 'sprint', '|', 'looseEndCount', '|', 'saveStatus', 'syncStatus', '|', 'account',
]
const PHONE_KEYBOARD = [
  'panelToggle', 'undo', 'redo', 'blockType', 'textColor', 'highlightColor', 'bold', 'italic', 'underline',
  'alignLeft', 'alignCenter', 'bulletList', 'taskList', 'link', 'details', 'looseEnd', 'history', 'fileMenu',
]

// Keys that were switched off by default before keys could move.
const OFF_BY_DEFAULT = new Set(['wordCount', 'looseEndCount'])

// The tab row's own keys (tabs In the toolbar); everything else from the top
// bar waits in its More tools drawer.
const DESKTOP_TAB_ROW = [
  'blockType', '|', 'bold', 'italic', 'underline', '|', 'bulletList', 'orderedList', '|', 'link', 'looseEnd', '|',
  'moreTools', 'windowControls',
]
const DESKTOP_TAB_DRAWER = [
  'panelToggle', 'undo', 'redo', 'history', 'fileMenu', '|',
  'fontFamily', 'fontSize', 'textColor', 'highlightColor', '|',
  'strike', 'subscript', 'superscript', 'code', 'clearFormatting', '|',
  'alignLeft', 'alignCenter', 'alignRight', 'alignJustify', 'taskList', '|',
  'horizontalRule', 'image', 'details', 'dragHandle', 'table',
]

const DEFAULT_SPECS: Record<BarDevice, BarSpec> = {
  desktop: {
    top: DESKTOP_TOP.filter((id) => !OFF_BY_DEFAULT.has(id)),
    bottom: DESKTOP_BOTTOM.filter((id) => !OFF_BY_DEFAULT.has(id)),
    right: TOOL_KEYS,
    tabRow: DESKTOP_TAB_ROW,
    tabDrawer: DESKTOP_TAB_DRAWER,
  },
  phone: {
    keyboard: PHONE_KEYBOARD,
    // Off by default; it starts with the keys it always offered.
    top: ['panelToggle', 'undo', 'redo', 'history', 'fileMenu'],
    side: [TOOLS_KEY, '|', 'findInPage', 'orderedList'],
    // The pop-up bars appear one at a time over the text, so they keep their own copies.
    selection: ['lookupReference', 'cut', 'copy', 'selectAll', 'bold', 'italic', 'underline', 'looseEnd'],
    tap: ['paste', 'pastePlain', 'undo', 'redo', 'selectAll', 'blockType', 'alignLeft', 'alignCenter', 'looseEnd', 'parkIdea'],
  },
}

function buildBars(device: BarDevice, spec: BarSpec): Record<string, string[]> {
  let piece = 0
  const bars: Record<string, string[]> = {}
  for (const bar of BARS[device]) {
    // A spec that leaves a bar out (presets older than the bar) gets its defaults.
    bars[bar.id] = (spec[bar.id] ?? DEFAULT_SPECS[device][bar.id] ?? []).map((id) => id === '|' ? `sep:${++piece}` : id === '~' ? `gap:${++piece}` : id)
  }
  return bars
}

export function defaultBarLayout(device: BarDevice): BarLayout {
  return { bars: buildBars(device, DEFAULT_SPECS[device]), home: {}, seen: barKeysFor(device).map((key) => key.id) }
}

/** Moves keys pinned to this bar to its end. */
function pinToEnd(barId: string, list: string[]): string[] {
  const pinned = list.filter((id) => KEYS_BY_ID.get(id)?.pinnedTo?.includes(barId))
  return pinned.length ? [...list.filter((id) => !pinned.includes(id)), ...pinned] : list
}

/** Puts a key beside its nearest neighbour from the default list, or at the end. */
function insertNearDefault(barId: string, list: string[], defaults: string[], id: string): string[] {
  const at = defaults.indexOf(id)
  if (at < 0) return pinToEnd(barId, [...list, id])
  for (let i = at - 1; i >= 0; i--) {
    const found = isBarPiece(defaults[i]) ? -1 : list.indexOf(defaults[i])
    if (found >= 0) return pinToEnd(barId, [...list.slice(0, found + 1), id, ...list.slice(found + 1)])
  }
  for (let i = at + 1; i < defaults.length; i++) {
    const found = isBarPiece(defaults[i]) ? -1 : list.indexOf(defaults[i])
    if (found >= 0) return pinToEnd(barId, [...list.slice(0, found), id, ...list.slice(found)])
  }
  return pinToEnd(barId, [...list, id])
}

/** Drops dividers at either end and side by side (a bar filled from defaults less some keys). */
function tidyDividers(list: string[]): string[] {
  const out: string[] = []
  for (const id of list) {
    if (id.startsWith('sep:') && (!out.length || out[out.length - 1].startsWith('sep:'))) continue
    out.push(id)
  }
  while (out.length && out[out.length - 1].startsWith('sep:')) out.pop()
  return out
}

/**
 * Repairs a layout from storage, sync or an import: drops unknown and
 * misplaced keys, repeats on one bar and repeated pieces, keeps pinned keys
 * at their end, and adds keys new in this version where the defaults put them.
 */
export function normalizeBarLayout(raw: any, device: BarDevice): BarLayout {
  if (!raw || typeof raw !== 'object' || !raw.bars || typeof raw.bars !== 'object') return defaultBarLayout(device)

  const usedPieces = new Set<string>()
  const bars: Record<string, string[]> = {}
  // A bar the layout has never had (new in this version) starts with its
  // defaults, less any key the writer has switched off. It is filled in after
  // the others, and doesn't count when working out what the writer has seen.
  const isFresh = (barId: string) => !Array.isArray(raw.bars[barId])
  const rawKeys = new Set<string>()
  for (const list of Object.values<any>(raw.bars)) {
    if (Array.isArray(list)) for (const id of list) if (typeof id === 'string') rawKeys.add(id)
  }
  const rawSeen = new Set<string>(Array.isArray(raw.seen) ? raw.seen.filter((id: unknown) => typeof id === 'string') : [])
  let nextPiece = Math.max(0, ...[...rawKeys].filter(isBarPiece).map((id) => Number(id.split(':')[1]) || 0))
  const fresh = buildBars(device, DEFAULT_SPECS[device])
  for (const bar of BARS[device]) {
    const out: string[] = []
    const source: unknown[] = isFresh(bar.id)
      ? fresh[bar.id]
        .filter((id) => isBarPiece(id) || rawKeys.has(id) || !rawSeen.has(id))
        .map((id) => isBarPiece(id) ? `${id.split(':')[0]}:${++nextPiece}` : id)
      : raw.bars[bar.id]
    for (const id of source) {
      if (typeof id !== 'string') continue
      if (isBarPiece(id)) {
        if (!usedPieces.has(id)) out.push(id)
        usedPieces.add(id)
      } else if (!out.includes(id) && canPlaceKey(device, id, bar.id)) {
        out.push(id)
      }
    }
    bars[bar.id] = pinToEnd(bar.id, isFresh(bar.id) ? tidyDividers(out) : out)
  }

  const known = new Set(barKeysFor(device).map((key) => key.id))
  const onBars = new Set(Object.values(bars).flat())
  const onKeptBars = new Set(BARS[device].filter((bar) => !isFresh(bar.id)).flatMap((bar) => bars[bar.id]))
  const seen = new Set<string>([...rawSeen].filter((id) => known.has(id)))
  for (const id of onKeptBars) if (!isBarPiece(id)) seen.add(id)

  const home: Record<string, BarHome> = {}
  for (const [id, h] of Object.entries<any>(raw.home && typeof raw.home === 'object' ? raw.home : {})) {
    if (!known.has(id) || onBars.has(id) || !h || !bars[h.bar]) continue
    if (h.after !== null && typeof h.after !== 'string') continue
    home[id] = { bar: h.bar, after: h.after }
  }

  // Keys new in this version go where the defaults put them; the rest stay off.
  const defaults = buildBars(device, DEFAULT_SPECS[device])
  for (const bar of BARS[device]) {
    for (const id of defaults[bar.id]) {
      if (isBarPiece(id) || seen.has(id) || bars[bar.id].includes(id)) continue
      bars[bar.id] = insertNearDefault(bar.id, bars[bar.id], defaults[bar.id], id)
    }
  }
  // Marked only after every bar has had its chance, so a key new to two bars lands on both.
  for (const id of known) seen.add(id)

  return { bars, home, seen: [...seen] }
}

/** Takes a key off one bar. A key left on no bar remembers where it was. */
export function removeBarKey(layout: BarLayout, barId: string, keyId: string): BarLayout {
  const list = layout.bars[barId] ?? []
  const at = list.indexOf(keyId)
  if (at < 0) return layout
  const bars = { ...layout.bars, [barId]: list.filter((id) => id !== keyId) }
  const home = { ...layout.home }
  if (!isBarPiece(keyId) && !Object.values(bars).some((items) => items.includes(keyId))) {
    home[keyId] = { bar: barId, after: at > 0 ? list[at - 1] : null }
  }
  return { ...layout, bars, home }
}

/** Puts a key back on a bar: where it last sat there, or beside its default neighbours. */
export function restoreBarKey(layout: BarLayout, device: BarDevice, barId: string, keyId: string): BarLayout {
  const list = layout.bars[barId] ?? []
  if (list.includes(keyId) || !canPlaceKey(device, keyId, barId)) return layout
  const saved = layout.home[keyId]
  let next: string[]
  if (saved?.bar === barId && (saved.after === null || list.includes(saved.after))) {
    const at = saved.after === null ? 0 : list.indexOf(saved.after) + 1
    next = pinToEnd(barId, [...list.slice(0, at), keyId, ...list.slice(at)])
  } else {
    next = insertNearDefault(barId, list, buildBars(device, DEFAULT_SPECS[device])[barId] ?? [], keyId)
  }
  const home = { ...layout.home }
  delete home[keyId]
  return { ...layout, bars: { ...layout.bars, [barId]: next }, home }
}

/** Why a key can't go where it was dropped, if it can't. */
export type PlaceRefusal = 'unknown' | 'tooWide' | 'pinned' | 'alreadyThere'

export function placeRefusal(layout: BarLayout, device: BarDevice, keyId: string, toBar: string, fromBar: string | null, copy: boolean): PlaceRefusal | null {
  if (isBarPiece(keyId)) return null
  const key = KEYS_BY_ID.get(keyId)
  const bar = BARS[device].find((b) => b.id === toBar)
  if (!key || !bar || (key.device && key.device !== device)) return 'unknown'
  if (key.pinnedTo && !key.pinnedTo.includes(toBar)) return 'pinned'
  if (key.acrossOnly && bar.shape === 'side') return 'tooWide'
  if ((layout.bars[toBar] ?? []).includes(keyId) && (copy || fromBar !== toBar)) return 'alreadyThere'
  return null
}

/**
 * Puts a key (or a piece) on a bar at an index: moved from another bar, kept
 * there too as a copy, or switched on from nowhere. The index counts the
 * bar's items without the moving key. A refused place leaves the layout as it was.
 */
export function placeBarKey(
  layout: BarLayout,
  device: BarDevice,
  keyId: string,
  toBar: string,
  index: number,
  { fromBar = null, copy = false }: { fromBar?: string | null; copy?: boolean } = {},
): BarLayout {
  if (placeRefusal(layout, device, keyId, toBar, fromBar, copy)) return layout
  const bars = { ...layout.bars }
  if (fromBar && !copy) bars[fromBar] = (bars[fromBar] ?? []).filter((id) => id !== keyId)
  const list = (bars[toBar] ?? []).filter((id) => id !== keyId)
  const at = Math.max(0, Math.min(index, list.length))
  bars[toBar] = pinToEnd(toBar, [...list.slice(0, at), keyId, ...list.slice(at)])
  const home = { ...layout.home }
  delete home[keyId]
  return { ...layout, bars, home }
}

/** A new divider or push-apart gap, numbered after every piece in the layout. */
export function newBarPiece(layout: BarLayout, kind: 'sep' | 'gap'): string {
  let highest = 0
  for (const id of Object.values(layout.bars).flat()) {
    if (isBarPiece(id)) highest = Math.max(highest, Number(id.split(':')[1]))
  }
  return `${kind}:${highest + 1}`
}

/** Where a switched-off key goes when switched back on: its old place, or its default bar. */
export function homeBarFor(layout: BarLayout, device: BarDevice, keyId: string): string | null {
  const saved = layout.home[keyId]
  if (saved && layout.bars[saved.bar] && canPlaceKey(device, keyId, saved.bar)) return saved.bar
  const defaults = buildBars(device, DEFAULT_SPECS[device])
  const bar = BARS[device].find((b) => defaults[b.id].includes(keyId) && canPlaceKey(device, keyId, b.id))
    ?? BARS[device].find((b) => canPlaceKey(device, keyId, b.id))
  return bar?.id ?? null
}

/** Ready-made layouts for each app. */
export const BAR_PRESETS: Record<BarDevice, Record<string, { label: string; description: string; spec: BarSpec }>> = {
  desktop: {
    default: { label: 'Default', description: 'Every formatting key on top, the page keys below, the tools at the side', spec: DEFAULT_SPECS.desktop },
    zen: { label: 'Zen', description: 'Undo, redo, the File menu and your save status', spec: {
      top: ['undo', 'redo', '|', 'fileMenu', '|', 'windowControls'],
      bottom: ['~', 'saveStatus', 'syncStatus'],
      right: TOOL_KEYS,
    } },
    writer: { label: 'Writer', description: 'Headings, bold, italic, lists and history; title and progress below', spec: {
      top: ['panelToggle', '|', 'undo', 'redo', 'history', '|', 'blockType', '|', 'bold', 'italic', '|', 'bulletList', 'orderedList', '|', 'looseEnd', '|', 'fileMenu', '|', 'windowControls'],
      bottom: ['docName', '~', 'wordCount', 'goal', 'sprint', '|', 'saveStatus', 'syncStatus', '|', 'account'],
      right: [...TOOL_KEYS, '|', 'findInPage'],
    } },
    formatting: { label: 'Formatting', description: 'The full formatting and typography suite, with weights and the clipboard at the side', spec: {
      top: DEFAULT_SPECS.desktop.top,
      bottom: DEFAULT_SPECS.desktop.bottom,
      right: [...TOOL_KEYS, '|', 'fontWeights', '|', 'cut', 'copy', 'paste', 'pastePlain', '|', 'findInPage', 'referenceManager'],
    } },
  },
  phone: {
    default: { label: 'Default', description: 'The keyboard bar full of formatting; the pop-ups with the clipboard', spec: DEFAULT_SPECS.phone },
    minimal: { label: 'Minimal', description: 'Undo, redo, bold, italic and the File menu', spec: {
      keyboard: ['undo', 'redo', '|', 'bold', 'italic', '~', 'fileMenu'],
      top: DEFAULT_SPECS.phone.top,
      side: [],
      selection: ['cut', 'copy', 'looseEnd'],
      tap: ['paste', 'selectAll'],
    } },
  },
}

export function presetBarLayout(device: BarDevice, presetId: string): BarLayout {
  const preset = BAR_PRESETS[device][presetId] ?? BAR_PRESETS[device].default
  return { bars: buildBars(device, preset.spec), home: {}, seen: barKeysFor(device).map((key) => key.id) }
}

/**
 * What a bar draws, given which keys can show right now: dividers at either
 * end, beside another divider or beside a gap are left out.
 */
export function visibleBarItems(items: readonly string[], isShown: (id: string) => boolean): string[] {
  const out: string[] = []
  for (const id of items) {
    if (id.startsWith('sep:')) {
      const last = out[out.length - 1]
      if (last && !last.startsWith('sep:') && !last.startsWith('gap:')) out.push(id)
    } else if (id.startsWith('gap:')) {
      if (out[out.length - 1]?.startsWith('sep:')) out.pop()
      out.push(id)
    } else if (isShown(id)) {
      out.push(id)
    }
  }
  while (out[out.length - 1]?.startsWith('sep:')) out.pop()
  return out
}

/** The toolbar settings from before keys could move. */
export interface LegacyBarSettings {
  toolbar?: { top?: Record<string, boolean>; bottom?: Record<string, boolean> }
  rightEdgeMode?: string
  rightBarItems?: unknown
  toolOrder?: readonly string[]
  goalReadoutShown?: boolean
  looseEndCountShown?: boolean
  mobileKeyboardToolbarItems?: unknown
  mobileStaticTopToolbarItems?: unknown
  mobileRightBarItems?: unknown
  selectionHighlightItems?: unknown
  selectionEmptyItems?: unknown
}

// Old names for keys that now share one.
const LEGACY_ALIASES: Record<string, string> = { colors: 'colorPickers', flagLooseEnd: 'looseEnd' }

function legacyList(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null
  return raw.filter((id): id is string => typeof id === 'string').map((id) => LEGACY_ALIASES[id] ?? id)
}

/** Builds a layout that shows exactly what the old settings showed. */
export function migrateBarLayout(device: BarDevice, old: LegacyBarSettings): BarLayout {
  const defaults = DEFAULT_SPECS[device]
  const spec: Record<string, string[]> = {}

  if (device === 'desktop') {
    const top = old.toolbar?.top ?? {}
    const bottom = old.toolbar?.bottom ?? {}
    const shown = (flags: Record<string, boolean>, id: string) => OFF_BY_DEFAULT.has(id) ? flags[id] === true : flags[id] !== false
    spec.top = DESKTOP_TOP.filter((id) => id === '|' || shown(top, id))
    spec.bottom = DESKTOP_BOTTOM.filter((id) => {
      if (id === 'goal') return old.goalReadoutShown !== false
      if (id === 'looseEndCount') return old.looseEndCountShown === true
      if (id === 'sprint') return true
      return id === '|' || shown(bottom, id)
    })
    if (old.rightEdgeMode === 'toolbar') spec.right = legacyList(old.rightBarItems) ?? []
    else if (old.rightEdgeMode === 'none') spec.right = []
    else spec.right = (old.toolOrder ?? TOOL_IDS).map((tool) => toolKey(tool as ToolId))
  } else {
    spec.keyboard = legacyList(old.mobileKeyboardToolbarItems) ?? [...defaults.keyboard]
    spec.top = legacyList(old.mobileStaticTopToolbarItems) ?? [...defaults.top]
    spec.side = legacyList(old.mobileRightBarItems) ?? [...defaults.side]
    // The selection bar always led with Look Up and ended with Loose End; the tap menu also offered Park an Idea.
    const withExtras = (items: string[], lead: string[], extras: string[]) => [
      ...lead.filter((id) => !items.includes(id)), ...items, ...extras.filter((id) => !items.includes(id)),
    ]
    const highlight = legacyList(old.selectionHighlightItems)
    const empty = legacyList(old.selectionEmptyItems)
    spec.selection = highlight ? withExtras(highlight, ['lookupReference'], ['looseEnd']) : [...defaults.selection]
    spec.tap = empty ? withExtras(empty, [], ['looseEnd', 'parkIdea']) : [...defaults.tap]
  }

  return normalizeBarLayout({ bars: buildBars(device, spec), home: {}, seen: barKeysFor(device).map((key) => key.id) }, device)
}
